import React, { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

// hooks
import useAuth from "hooks/useAuth";
import useExpensesTable from "./hooks/useExpensesTable";
import useSuppliersTable from "./hooks/useSuppliersTable";
import useLeviesTable from "./hooks/useLeviesTable";
import useRecurringTable from "./hooks/useRecurringTable";

// reusable components
import { SecureTableShell } from "@/components/ui/SecureTableShell";
import EmptyStateCard from "@/components/ui/EmptyStateCard";
import PillTabs from "@/components/ui/PillTabs";
import ExpenseSearchControls from "./components/ExpenseSearchControls";
import SimpleSearchControls from "./components/SimpleSearchControls";
import FinanceSummary from "./components/FinanceSummary";

// utils
import { getFallbackStateFinance } from "./utils/getFallbackStateFinance";

// Shared error fallback
const ErrorFallback = ({ label, onAction }) => (
  <EmptyStateCard
    title={`Failed to load ${label}`}
    description="Please try again later."
    buttonLabel="Retry"
    onAction={onAction}
  />
);

const noActionMessages = (noDataTitle, noDataDesc) => ({
  noData: { title: noDataTitle, description: noDataDesc, buttonLabel: null },
  noMatch: {
    title: "No matching records",
    description: "Try adjusting your search or filters.",
    buttonLabel: null,
  },
});

const ExpensesTab = () => {
  const {
    allExpenses,
    expenses: filteredExpenses,
    searchTerm,
    updateSearchTerm,
    stateFilter,
    updateStateFilter,
    paymentFilter,
    updatePaymentFilter,
    expenseColumns,
    renderExpenseRow,
    isLoading,
    isError,
    refetch,
  } = useExpensesTable();

  const [rawSearchTerm, setRawSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const timeout = setTimeout(() => updateSearchTerm(rawSearchTerm), 600);
    return () => clearTimeout(timeout);
  }, [rawSearchTerm, updateSearchTerm]);

  const fallback = getFallbackStateFinance({
    isLoading,
    allData: allExpenses,
    filteredData: filteredExpenses,
  });

  if (isError) return <ErrorFallback label="expenses" onAction={refetch} />;

  return (
    <>
      <FinanceSummary expenses={allExpenses} />
      <ExpenseSearchControls
        searchTerm={rawSearchTerm}
        updateSearchTerm={setRawSearchTerm}
        stateFilter={stateFilter}
        updateStateFilter={updateStateFilter}
        paymentFilter={paymentFilter}
        updatePaymentFilter={updatePaymentFilter}
      />
      <SecureTableShell
        data={filteredExpenses}
        columns={expenseColumns}
        renderRow={renderExpenseRow}
        fallbackState={fallback.state}
        fallbackMessages={noActionMessages(
          "No expenses recorded",
          "Recorded expenses and bills will appear here."
        )}
      />
    </>
  );
};

const SuppliersTab = () => {
  const [rawSearchTerm, setRawSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(rawSearchTerm), 400);
    return () => clearTimeout(timeout);
  }, [rawSearchTerm]);

  const {
    allSuppliers,
    suppliers: filteredSuppliers,
    supplierColumns,
    renderSupplierRow,
    isLoading,
    isError,
    refetch,
  } = useSuppliersTable(debounced);

  const fallback = getFallbackStateFinance({
    isLoading,
    allData: allSuppliers,
    filteredData: filteredSuppliers,
  });

  if (isError) return <ErrorFallback label="suppliers" onAction={refetch} />;

  return (
    <>
      <SimpleSearchControls
        searchTerm={rawSearchTerm}
        updateSearchTerm={setRawSearchTerm}
        placeholder="Search suppliers by name, email or tax number..."
      />
      <SecureTableShell
        data={filteredSuppliers}
        columns={supplierColumns}
        renderRow={renderSupplierRow}
        fallbackState={fallback.state}
        fallbackMessages={noActionMessages(
          "No suppliers yet",
          "Your suppliers and their balances will appear here."
        )}
      />
    </>
  );
};

const LeviesTab = () => {
  const [rawSearchTerm, setRawSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(rawSearchTerm), 400);
    return () => clearTimeout(timeout);
  }, [rawSearchTerm]);

  const {
    allLevies,
    levies: filteredLevies,
    levyColumns,
    renderLevyRow,
    isLoading,
    isError,
    refetch,
  } = useLeviesTable(debounced);

  const fallback = getFallbackStateFinance({
    isLoading,
    allData: allLevies,
    filteredData: filteredLevies,
  });

  if (isError) return <ErrorFallback label="levies" onAction={refetch} />;

  return (
    <>
      <SimpleSearchControls
        searchTerm={rawSearchTerm}
        updateSearchTerm={setRawSearchTerm}
        placeholder="Search levies and taxes..."
      />
      <SecureTableShell
        data={filteredLevies}
        columns={levyColumns}
        renderRow={renderLevyRow}
        fallbackState={fallback.state}
        fallbackMessages={noActionMessages(
          "No levies configured",
          "Taxes and levies applied to sales will appear here."
        )}
      />
    </>
  );
};

const RecurringTab = () => {
  const [rawSearchTerm, setRawSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(rawSearchTerm), 400);
    return () => clearTimeout(timeout);
  }, [rawSearchTerm]);

  const {
    allRecurring,
    recurring: filteredRecurring,
    recurringColumns,
    renderRecurringRow,
    isLoading,
    isError,
    refetch,
  } = useRecurringTable(debounced);

  const fallback = getFallbackStateFinance({
    isLoading,
    allData: allRecurring,
    filteredData: filteredRecurring,
  });

  if (isError) return <ErrorFallback label="recurring expenses" onAction={refetch} />;

  return (
    <>
      <SimpleSearchControls
        searchTerm={rawSearchTerm}
        updateSearchTerm={setRawSearchTerm}
        placeholder="Search recurring expenses..."
      />
      <SecureTableShell
        data={filteredRecurring}
        columns={recurringColumns}
        renderRow={renderRecurringRow}
        fallbackState={fallback.state}
        fallbackMessages={noActionMessages(
          "No recurring expenses",
          "Scheduled, repeating expenses will appear here."
        )}
      />
    </>
  );
};

const TABS = [
  { id: "expenses", label: "Expenses" },
  { id: "suppliers", label: "Suppliers" },
  { id: "levies", label: "Levies" },
  { id: "recurring", label: "Recurring" },
];

const VALID_TABS = TABS.map((t) => t.id);

const Finance = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : "expenses";

  const setActiveTab = (id) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", id);
    // clear tab-specific filters when switching
    params.delete("search");
    params.delete("state");
    params.delete("payment");
    setSearchParams(params);
  };

  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  return (
    <div>
      <PillTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {activeTab === "expenses" && <ExpensesTab />}
      {activeTab === "suppliers" && <SuppliersTab />}
      {activeTab === "levies" && <LeviesTab />}
      {activeTab === "recurring" && <RecurringTab />}
    </div>
  );
};

export default Finance;
